/**
 * What the refinement did, and the way back.
 *
 * Sits above the box rather than in the settings row below it, because it
 * describes text the reader is looking at — and because the swap happens
 * without a page transition, a reader who glanced away needs to be told their
 * words changed. `role="status"` is what tells a screen-reader user the same
 * thing; without it the box silently contains something they did not type.
 */

import { Undo2, Wand2 } from "lucide-react";
import { Button } from "../ui/button.tsx";

interface RefineNoteProps {
  readonly note: string;
  /** Absent when nothing changed — there is then nothing to undo. */
  readonly onRevert?: (() => void) | undefined;
}

export function RefineNote({ note, onRevert }: RefineNoteProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-start gap-2 px-1 font-sans text-xs text-muted-foreground"
    >
      <Wand2 className="mt-0.5 size-3.5 shrink-0 text-primary" aria-hidden="true" />
      <span className="min-w-0 flex-1">{note}</span>

      {onRevert && (
        <Button
          variant="ghost"
          size="xs"
          onClick={onRevert}
          className="-my-0.5 shrink-0 gap-1 text-muted-foreground hover:text-foreground"
        >
          <Undo2 />
          Revert
        </Button>
      )}
    </div>
  );
}
